import React, { useEffect, useMemo, useState } from "react";
import { Alert, ButtonBase } from "@mui/material";
import { generatePath, Link as RouterLink } from "react-router-dom";
import {
  CmpProspectFieldsFragment,
  CmpProspectStatusEnum,
  GetCmpManageProspectsQuery,
  useGetCmpManageProspectsQuery,
  useGetCmpProspectMemberMatchOptionsQuery,
  useMatchCmpProspectToMemberMutation,
} from "../../../../graphql/types";
import { RoutePaths } from "../../../../enumStrings";
import useGetChamberFromUserIdInAuth from "../../../../hooks/useGetChamberFromUserIdInAuth";
import { useCmpOrganizationNoun } from "../../../../contexts/CmpOrganizationNounContext";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
} from "../../../../utils/userFacingError";
import {
  DataTable,
  DataTableColumn,
  SortDirection,
} from "../../../common/dashboard/DataTable";
import { EmptyState } from "../../../common/dashboard/EmptyState";
import { FilterSearchField } from "../../../common/dashboard/FilterBar";
import { cmpShortDateFormatter } from "../../../common/dashboard/formatters";
import { LoadingOverlay } from "../../../common/dashboard/LoadingOverlay";
import { ManagePageShell } from "../../../common/dashboard/ManagePageShell";
import {
  InvoiceDesignScope,
  invoiceDesignFontFamily,
  invoiceDesignTokens,
} from "../../Invoicing/InvoiceDesignSystem";
import ProspectMatchMemberDialog from "../ProspectMatchMemberDialog";
import ProspectResultDialog from "../ProspectResultDialog";
import {
  ProspectStatusFilter,
  ProspectStatusTabs,
} from "../ProspectStatusTabs";
import { getCmpProspectStatusLabel, isCmpProspectNewLead } from "../prospectStatusLabel";
import { prospectDesignFontFamily, prospectHeaderStyles, ProspectSortGlyph } from "../prospectDesign";
import { PROSPECT_ERROR_CONTEXT } from "../prospectFormModel";
import {
  acknowledgeRecentlyCreatedProspect,
  getRecentlyCreatedProspects,
} from "../prospectRecentlyCreated";

type ProspectRow =
  GetCmpManageProspectsQuery["getCmpManageProspects"]["prospects"][number];
type SortableColumnKey =
  | "companyName"
  | "mainContactName"
  | "mainContactEmail"
  | "leadCaptureLabel"
  | "createdByName"
  | "createdAtUtcMilli"
  | "status"
  | "convertedAtUtcMilli";
type ColumnKey = SortableColumnKey | "action" | "";
type SortState = { column: SortableColumnKey; direction: SortDirection } | null;

const normalizeSearchText = (value?: string | null) =>
  (value || "").trim().toLocaleLowerCase();
const getProspectSearchText = (prospect: ProspectRow) =>
  [
    prospect.companyName,
    prospect.companyWebsite,
    prospect.mainContactName,
    prospect.mainContactTitle,
    prospect.mainContactEmail,
    prospect.mainPhoneNumber,
    prospect.mainPhoneNumberExtension,
    prospect.mobilePhoneNumber,
    prospect.leadCaptureLabel,
    prospect.createdByName,
  ]
    .filter(Boolean)
    .join(" ")
    .toLocaleLowerCase();

const getProspectSortValue = (
  prospect: ProspectRow,
  column: SortableColumnKey,
): string | number => {
  if (column === "createdAtUtcMilli" || column === "convertedAtUtcMilli")
    return prospect[column] || 0;
  if (column === "status") return getCmpProspectStatusLabel(prospect);
  return normalizeSearchText(prospect[column]);
};
const prospectPath = (id: string) =>
  generatePath(RoutePaths.chamberAdmin_prospects_update, { prospectId: id });
const stickyShadow = "-6px 0 8px -6px rgba(0,0,0,.12)";
const tableGrid =
  "minmax(160px,1.1fr) minmax(130px,.9fr) minmax(200px,1.4fr) minmax(140px,1fr) minmax(120px,.8fr) minmax(120px,.9fr) minmax(150px,1fr) minmax(130px,.9fr) 150px";
const actionSx = {
  backgroundColor: invoiceDesignTokens.gold,
  borderRadius: "7px",
  color: "#FFFFFF",
  fontFamily: invoiceDesignFontFamily,
  fontSize: 13,
  fontWeight: 600,
  lineHeight: "normal",
  width: 124,
  height: 34,
  flexShrink: 0,
  "&:hover": { backgroundColor: invoiceDesignTokens.goldDark },
  "&.Mui-focusVisible": { outline: "2px solid #0A0A0A", outlineOffset: 2 },
} as const;
const ellipsis = (value?: string | null, bold = false) => (
  <span
    className={`block overflow-hidden text-ellipsis whitespace-nowrap ${bold ? "font-semibold" : ""}`}
    title={value || "-"}
  >
    {value || "-"}
  </span>
);

const ManageProspects = () => {
  const noun = useCmpOrganizationNoun();
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";
  const [searchQuery, setSearchQuery] = useState("");
  const [status, setStatus] = useState<ProspectStatusFilter>("All");
  const [sort, setSort] = useState<SortState>(null);
  const [recentProspects, setRecentProspects] = useState<
    CmpProspectFieldsFragment[]
  >([]);
  // Conversion is final. Keep its confirmed mutation snapshot for this page
  // visit: Apollo normalization can update the list before the GSI catches up.
  const [matchedProspects, setMatchedProspects] = useState<
    CmpProspectFieldsFragment[]
  >([]);
  const [matchingProspect, setMatchingProspect] = useState<ProspectRow | null>(
    null,
  );
  const [matchError, setMatchError] = useState("");
  const [matchResult, setMatchResult] = useState<{
    companyName: string;
    memberName: string;
  } | null>(null);
  const variables = useMemo(() => ({ input: { chamberId } }), [chamberId]);
  const { data, loading, error, refetch } = useGetCmpManageProspectsQuery({
    context: PROSPECT_ERROR_CONTEXT,
    variables,
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });
  const {
    data: memberData,
    loading: membersLoading,
    error: membersError,
  } = useGetCmpProspectMemberMatchOptionsQuery({
    context: PROSPECT_ERROR_CONTEXT,
    variables,
    skip: !chamberId || !matchingProspect,
    fetchPolicy: "network-only",
  });
  const [matchProspect, { loading: matching }] =
    useMatchCmpProspectToMemberMutation({
      context: PROSPECT_ERROR_CONTEXT,
    });
  const serverProspects = useMemo(
    () => data?.getCmpManageProspects.prospects || [],
    [data],
  );
  const memberOptions = memberData?.getCmpProspectMemberMatchOptions || [];

  useEffect(() => {
    setRecentProspects(chamberId ? getRecentlyCreatedProspects(chamberId) : []);
    setMatchedProspects([]);
  }, [chamberId]);

  useEffect(() => {
    const acknowledged = new Set(
      serverProspects.map((prospect) => prospect.id),
    );
    serverProspects.forEach((prospect) =>
      acknowledgeRecentlyCreatedProspect(chamberId, prospect.id),
    );
    setRecentProspects((current) => {
      const next = current.filter(
        (prospect) =>
          prospect.chamberId === chamberId && !acknowledged.has(prospect.id),
      );
      return next.length === current.length ? current : next;
    });
  }, [chamberId, serverProspects]);

  const recentIds = recentProspects
    .filter((prospect) => prospect.chamberId === chamberId)
    .map((prospect) => prospect.id)
    .join(",");
  useEffect(() => {
    if (!recentIds || !chamberId) return;
    let cancelled = false;
    let attempt = 0;
    let timeoutId: number;
    const check = async () => {
      if (cancelled) return;
      try {
        const result = await refetch(variables);
        const returnedIds = new Set(
          result.data.getCmpManageProspects.prospects.map(
            (prospect) => prospect.id,
          ),
        );
        if (recentIds.split(",").every((id) => returnedIds.has(id))) return;
      } catch (_) {
        // The normal query error remains visible during bounded GSI retries.
      }
      attempt += 1;
      if (!cancelled && attempt < 6)
        timeoutId = window.setTimeout(() => void check(), 500);
    };
    timeoutId = window.setTimeout(() => void check(), 500);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [chamberId, recentIds, refetch, variables]);

  const allProspects = useMemo(() => {
    const knownIds = new Set(serverProspects.map((prospect) => prospect.id));
    const confirmedMatches = new Map(
      matchedProspects
        .filter((prospect) => prospect.chamberId === chamberId)
        .map((prospect) => [prospect.id, prospect]),
    );
    const merged = [
      ...recentProspects.filter(
        (prospect) =>
          prospect.chamberId === chamberId && !knownIds.has(prospect.id),
      ),
      ...serverProspects,
    ].map((prospect) => confirmedMatches.get(prospect.id) || prospect);
    const mergedIds = new Set(merged.map((prospect) => prospect.id));
    return [
      ...Array.from(confirmedMatches.values()).filter(
        (prospect) => !mergedIds.has(prospect.id),
      ),
      ...merged,
    ];
  }, [chamberId, matchedProspects, recentProspects, serverProspects]);

  const prospects = useMemo(() => {
    const search = normalizeSearchText(searchQuery);
    const filtered = allProspects.filter(
      (prospect) =>
        (!search || getProspectSearchText(prospect).includes(search)) &&
        (status === "All" ||
          prospect.status ===
            (status === "Active"
              ? CmpProspectStatusEnum.Active
              : CmpProspectStatusEnum.Converted)),
    );
    if (!sort) return filtered;
    return [...filtered].sort((first, second) => {
      const a = getProspectSortValue(first, sort.column);
      const b = getProspectSortValue(second, sort.column);
      const result =
        typeof a === "number" && typeof b === "number"
          ? a - b
          : String(a).localeCompare(String(b), "en-CA", {
              numeric: true,
              sensitivity: "base",
            });
      return sort.direction === "desc" ? -result : result;
    });
  }, [allProspects, searchQuery, sort, status]);

  const headerOverride = useMemo(
    () => ({
      ...prospectHeaderStyles,
      descriptionStyle: {
        ...prospectHeaderStyles.descriptionStyle,
        maxWidth: "none",
      },
      title: "Manage Prospects",
      description:
        "Track and manage prospective members. View prospect details, and convert prospects to members.",
    }),
    [],
  );
  const columns = useMemo<DataTableColumn<ProspectRow, ColumnKey>[]>(
    () => [
      {
        key: "companyName",
        header: "Prospect Company",
        sortable: true,
        render: (prospect) => (
          <RouterLink
            className="block overflow-hidden text-ellipsis whitespace-nowrap font-semibold underline"
            title={prospect.companyName}
            to={prospectPath(prospect.id)}
          >
            {prospect.companyName}
          </RouterLink>
        ),
      },
      {
        key: "mainContactName",
        header: "Contact",
        sortable: true,
        render: (prospect) => ellipsis(prospect.mainContactName),
      },
      {
        key: "mainContactEmail",
        header: "Email",
        sortable: true,
        render: (prospect) => ellipsis(prospect.mainContactEmail),
      },
      {
        key: "leadCaptureLabel",
        header: "Lead Capture",
        sortable: true,
        render: (prospect) => ellipsis(prospect.leadCaptureLabel, true),
      },
      {
        key: "createdByName",
        header: "Created by",
        sortable: true,
        render: (prospect) => ellipsis(prospect.createdByName),
      },
      {
        key: "createdAtUtcMilli",
        header: "Created Date",
        sortable: true,
        cellSx: { whiteSpace: "nowrap" },
        render: (prospect) =>
          cmpShortDateFormatter.format(prospect.createdAtUtcMilli),
      },
      {
        key: "status",
        header: "Status",
        sortable: true,
        cellSx: { padding: "12px 14px !important" },
        render: (prospect) => (
          <span
            data-testid="prospect-status"
            style={
              prospect.status === CmpProspectStatusEnum.Converted
                ? { color: "#10B981", display: "block", lineHeight: 1.4 }
                : isCmpProspectNewLead(prospect)
                  ? { color: "#F21B1B", fontWeight: 600, whiteSpace: "nowrap" }
                  : { color: "#0A0A0A", whiteSpace: "nowrap" }
            }
          >
            {getCmpProspectStatusLabel(prospect)}
          </span>
        ),
      },
      {
        key: "convertedAtUtcMilli",
        header: "Became a Member",
        sortable: true,
        cellSx: { whiteSpace: "nowrap" },
        render: (prospect) =>
          prospect.convertedAtUtcMilli
            ? cmpShortDateFormatter.format(prospect.convertedAtUtcMilli)
            : "-",
      },
      {
        key: "action",
        header: "Action",
        stickyRight: true,
        stickyRightSx: {
          borderLeft: 0,
          boxShadow: stickyShadow,
          backgroundColor: "#FFFFFF",
          "thead &": { backgroundColor: "#FAFAFA" },
        },
        cellSx: {
          alignSelf: "stretch",
          display: "flex",
          alignItems: "center",
          padding: "12px 14px !important",
        },
        render: (prospect) =>
          prospect.status === CmpProspectStatusEnum.Converted ? (
            <ButtonBase
              component={RouterLink}
              to={prospectPath(prospect.id)}
              sx={{
                ...actionSx,
                width: 110,
                backgroundColor: "#23272F",
                "&:hover": { backgroundColor: "#0A0A0A" },
              }}
            >
              View Details
            </ButtonBase>
          ) : (
            <div className="flex flex-col gap-[6px]">
              <ButtonBase
                component={RouterLink}
                to={prospectPath(prospect.id)}
                sx={actionSx}
              >
                Update
              </ButtonBase>
              <ButtonBase
                onClick={() => {
                  setMatchError("");
                  setMatchingProspect(prospect);
                }}
                sx={{
                  ...actionSx,
                  height: 30,
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #9E9E9E",
                  color: "#0A0A0A",
                  fontSize: 12,
                  "&:hover": { backgroundColor: "#F7F7F8" },
                }}
              >
                Match to Member
              </ButtonBase>
            </div>
          ),
      },
    ],
    [],
  );

  const analytics = data?.getCmpManageProspects.analytics;
  const metrics = analytics
    ? [
        {
          label: "Total Number of Prospect",
          value: analytics.totalProspectsYtd,
        },
        {
          label: "Prospects Converted to Members",
          value: analytics.convertedToMembersYtd,
        },
      ]
    : [];
  const sectionTitleClass =
    "text-[17px] font-semibold leading-[normal] text-[#0A0A0A]";

  const handleMatch = async (memberId: string) => {
    if (!matchingProspect || !chamberId) return;
    const member = memberOptions.find((option) => option.id === memberId);
    if (!member) {
      setMatchError("This member is no longer in your member list. Select another member.");
      return;
    }
    setMatchError("");
    try {
      const result = await matchProspect({
        variables: {
          input: { chamberId, prospectId: matchingProspect.id, memberId },
        },
      });
      if (!result.data?.matchCmpProspectToMember.prospectSavedSuccessfully) {
        throw new Error("Could not match the prospect to the member.");
      }
      const companyName = matchingProspect.companyName;
      acknowledgeRecentlyCreatedProspect(chamberId, matchingProspect.id);
      const confirmedProspect = result.data.matchCmpProspectToMember.prospect;
      setMatchedProspects((current) => [
        confirmedProspect,
        ...current.filter((prospect) => prospect.id !== confirmedProspect.id),
      ]);
      setRecentProspects((current) =>
        current.filter((prospect) => prospect.id !== matchingProspect.id),
      );
      setMatchingProspect(null);
      setMatchResult({ companyName, memberName: member.name });
      void refetch(variables).catch(() => undefined);
    } catch (failure) {
      setMatchError(
        getUserFacingErrorText(
          failure,
          buildStaffErrorFallback(
            `match ${matchingProspect.companyName} to ${member.name}`,
          ),
        ),
      );
    }
  };

  return (
    <InvoiceDesignScope fontFamily={prospectDesignFontFamily}>
      <ManagePageShell
        className="flex w-full flex-col gap-6 pb-10 pt-4"
        headerOverride={headerOverride}
      >
        <section className="flex flex-col gap-3 text-[#0A0A0A]">
          <div>
            <h2 className={sectionTitleClass}>
              Prospect Analytics to (Fiscal Year to Date)
            </h2>
            <p className="mt-1 text-[14px] leading-[normal]">
              See a quick overview of your prospect pipeline and engagement
            </p>
          </div>
          <div className="grid max-w-[960px] grid-cols-1 gap-4 md:grid-cols-3">
            {metrics.length ? (
              metrics.map((metric) => (
                <article
                  className="row-span-3 grid grid-rows-subgrid items-start gap-y-[10px] rounded-[10px] border border-[#ECECEC] px-[18px] py-4"
                  key={metric.label}
                >
                  <h3 className="text-[14px] font-semibold leading-[normal]">
                    {metric.label}
                  </h3>
                  <p className="whitespace-nowrap text-[18px] font-semibold leading-[normal]">
                    {metric.value.toLocaleString("en-CA")}
                  </p>
                </article>
              ))
            ) : (
              <div className="col-span-full rounded-[10px] border border-[#ECECEC] px-[18px] py-4 text-[14px]">
                {!chamberId || loading ? (
                  <LoadingOverlay label="Loading fiscal year analytics..." />
                ) : (
                  "We could not load your prospect totals. Refresh the page to try again."
                )}
              </div>
            )}
          </div>
        </section>
        <section className="flex flex-col gap-[10px]">
          <div>
            <h2 className={sectionTitleClass}>Prospect Filters</h2>
            <p className="mt-1 text-[14px] leading-[normal]">
              Search prospect name or contact details
            </p>
          </div>
          <FilterSearchField
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Enter prospect name or prospect details"
            className="w-full"
            adornmentIcon={
              <svg
                aria-hidden="true"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#9E9E9E"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
            }
            fieldSx={{
              "& .MuiOutlinedInput-root": {
                height: "auto",
                minHeight: "0 !important",
                borderRadius: "8px",
                padding: 0,
                fontFamily: invoiceDesignFontFamily,
                fontSize: 14,
                lineHeight: "normal",
                letterSpacing: "normal",
                color: "#0A0A0A",
                "& fieldset": { display: "none" },
                "&:hover:not(.Mui-focused) .MuiInputBase-input": {
                  borderColor: "#9E9E9E",
                },
                "&.Mui-focused .MuiInputBase-input": {
                  borderColor: invoiceDesignTokens.gold,
                },
              },
              "& .MuiInputAdornment-root": {
                position: "absolute",
                left: "14px",
                top: "50%",
                transform: "translateY(-50%)",
                margin: 0,
                pointerEvents: "none",
              },
              "& .MuiInputBase-input": {
                boxSizing: "border-box",
                height: "auto",
                padding: "12px 14px 12px 40px",
                fontSize: 14,
                lineHeight: "normal",
                letterSpacing: "normal",
                border: "1px solid #9E9E9E",
                borderRadius: "8px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
              "& .MuiInputBase-input::placeholder": {
                color: "#9E9E9E",
                opacity: 1,
              },
            }}
          />
          <ProspectStatusTabs value={status} onChange={setStatus} />
        </section>
        {!chamberId && (
          <Alert severity="info">
            Loading your {noun.singular} details. Your prospects will show in a moment.
          </Alert>
        )}
        {error && (
          <Alert severity="error">
            {getUserFacingErrorText(
              error,
              buildStaffErrorFallback("load your prospects"),
            )}
          </Alert>
        )}
        <section className="flex min-w-0 flex-col gap-[10px]">
          <h2 className="text-[17px] font-semibold leading-[normal] text-black">Prospect List</h2>
          <DataTable<ProspectRow, ColumnKey>
            ariaLabel="manage prospects table"
            columns={columns}
            rows={prospects}
            keepFeedbackInViewport
            getRowKey={(prospect) => prospect.id}
            containerClassName="w-full overflow-x-auto rounded-[10px] border border-[#ECECEC] bg-white"
            headClassName="bg-[#FAFAFA]"
            rowClassName=""
            tableSx={{
              width: "100%",
              minWidth: 1150,
              display: "block",
              "& thead, & tbody": { display: "block" },
              "& tr": {
                display: "grid",
                gridTemplateColumns: tableGrid,
                alignItems: "center",
                borderBottom: "1px solid #ECECEC",
              },
              "& th, & td": {
                boxSizing: "border-box",
                minWidth: 0,
                padding: "16px 14px",
                borderBottom: 0,
                color: "#0A0A0A",
                fontFamily: invoiceDesignFontFamily,
                lineHeight: "normal",
                overflowWrap: "normal",
              },
              "&& .MuiTableCell-root": {
                fontSize: "14px !important",
                letterSpacing: "normal",
              },
              "& th": {
                padding: "14px",
                fontWeight: 600,
                alignSelf: "stretch",
                display: "flex",
                alignItems: "center",
              },
              "&& th:last-child": {
                display: "block",
                fontSize: "13px !important",
              },
              "& td[colspan]": { gridColumn: "1 / -1" },
            }}
            sort={{
              column: sort?.column || "",
              direction: sort?.direction || "asc",
              onRequestSort: (column) => {
                if (column === "action" || column === "") return;
                setSort((current) =>
                  current?.column === column
                    ? current.direction === "asc"
                      ? { column, direction: "desc" }
                      : null
                    : { column, direction: "asc" },
                );
              },
            }}
            renderSortLabel={({
              column,
              label,
              active,
              direction,
              onRequestSort,
            }) => (
              <ButtonBase
                aria-label={`Sort ${label}`}
                onClick={() => onRequestSort(column)}
                sx={{
                  color: "#000000",
                  fontFamily: invoiceDesignFontFamily,
                  fontSize: 14,
                  fontWeight: 600,
                  lineHeight: "normal",
                  gap: "6px",
                  textAlign: "left",
                }}
              >
                {label}
                <ProspectSortGlyph active={active} direction={direction} />
              </ButtonBase>
            )}
            showLoadingRow={
              !chamberId || (loading && allProspects.length === 0)
            }
            loadingRowContent={<LoadingOverlay label="Loading prospects..." />}
            showEmptyRow={
              Boolean(chamberId) && !loading && !error && prospects.length === 0
            }
            emptyRowContent={
              <EmptyState
                message={
                  allProspects.length === 0
                    ? "You have no prospects yet. Go to Prospect Leads > Create Prospect to add one."
                    : "No prospects match your search or the selected status. Clear the search or select All."
                }
              />
            }
          />
        </section>
        <ProspectMatchMemberDialog
          open={Boolean(matchingProspect)}
          companyName={matchingProspect?.companyName || ""}
          members={memberOptions}
          loading={membersLoading}
          saving={matching}
          error={
            matchError ||
            (membersError
              ? getUserFacingErrorText(
                  membersError,
                  buildStaffErrorFallback("load the member list"),
                )
              : undefined)
          }
          onClose={() => {
            if (!matching) setMatchingProspect(null);
          }}
          onMatch={handleMatch}
        />
        <ProspectResultDialog
          open={Boolean(matchResult)}
          kind="matched"
          companyName={matchResult?.companyName || ""}
          memberName={matchResult?.memberName}
          onClose={() => setMatchResult(null)}
        />
      </ManagePageShell>
    </InvoiceDesignScope>
  );
};

export default ManageProspects;
